import { artH, artW } from './pixel';
import type { Prop } from './terrain';

const CELL = 128;
const key = (cx: number, cy: number): number => (cy + 4096) * 8192 + (cx + 4096);

/**
 * Props bucketed by screen cell, so a frame looks only at those near the view rather than at
 * every tree, rock and grass clump on the map. Each prop is filed by its top-left corner; a
 * query widens by the largest prop so nothing reaching into view is missed. A prop's art size is
 * taken once, when filed: one whose image is swapped (a grass clump bending) keeps its size.
 */
export class PropGrid {
  private readonly cells = new Map<number, number[]>();
  private readonly w: Float32Array;
  private readonly h: Float32Array;
  private maxW = 0;
  private maxH = 0;
  private readonly found: number[] = [];

  constructor(
    readonly props: readonly Prop[],
    size: (pr: Prop) => readonly [number, number] = (pr) => [artW(pr.img), artH(pr.img)],
  ) {
    this.w = new Float32Array(props.length);
    this.h = new Float32Array(props.length);
    props.forEach((pr, i) => {
      const [w, h] = size(pr);
      this.w[i] = w;
      this.h[i] = h;
      this.maxW = Math.max(this.maxW, w);
      this.maxH = Math.max(this.maxH, h);
      const k = key(Math.floor(pr.x / CELL), Math.floor(pr.y / CELL));
      let cell = this.cells.get(k);
      if (!cell) this.cells.set(k, (cell = []));
      cell.push(i);
    });
  }

  /**
   * Indices of the props overlapping the rectangle (world pixels), in their original order. The
   * array is reused by the next query.
   */
  query(x0: number, x1: number, y0: number, y1: number): readonly number[] {
    const found = this.found;
    found.length = 0;
    const cx0 = Math.floor((x0 - this.maxW) / CELL);
    const cx1 = Math.floor(x1 / CELL);
    const cy0 = Math.floor((y0 - this.maxH) / CELL);
    const cy1 = Math.floor(y1 / CELL);
    for (let cy = cy0; cy <= cy1; cy++)
      for (let cx = cx0; cx <= cx1; cx++) {
        const cell = this.cells.get(key(cx, cy));
        if (!cell) continue;
        for (const i of cell) {
          const pr = this.props[i]!;
          if (pr.x + this.w[i]! < x0 || pr.x > x1 || pr.y + this.h[i]! < y0 || pr.y > y1) continue;
          found.push(i);
        }
      }
    // Cells are visited row by row; the original order keeps equal-depth props drawn as before.
    found.sort((a, b) => a - b);
    return found;
  }

  /** Art size of prop `i`, as filed. */
  width(i: number): number {
    return this.w[i]!;
  }
  height(i: number): number {
    return this.h[i]!;
  }
}
